<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMobile } from '../composables/useMobile';
import Menu from './icons/Menu.vue';
import X from './icons/X.vue';
import Plus from './icons/Plus.vue';
import Download from './icons/Download.vue';
import Upload from './icons/Upload.vue';

import ChevronLeft from './icons/ChevronLeft.vue';
import Settings from './icons/Settings.vue';
import Users from './icons/Users.vue';
import HandHelping from './icons/HandHelping.vue';
import MessageSquare from './icons/MessageSquare.vue';

interface Props {
  filteredCount: number;
  totalIdeas: number;
  menuOpen: boolean;
  workshopName?: string;
  isOwner?: boolean;
  canWrite?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  workshopName: '',
  isOwner: false,
  canWrite: false,
});

const emit = defineEmits({
  toggleMenu: () => true,
  addIdea: () => true,
  export: () => true,
  import: () => true,
  reset: () => true,
  openSettings: () => true,
  openInvites: () => true,
  openSupporters: () => true,
  openComments: () => true,
});

const router = useRouter();
const { isMobile } = useMobile();

const lastScrollY = ref(0);
const isHidden = ref(false);

function handleScroll() {
  const currentScrollY = window.scrollY;
  const threshold = 50;

  if (currentScrollY < threshold) {
    isHidden.value = false;
  } else if (currentScrollY > lastScrollY.value) {
    isHidden.value = true;
  } else if (currentScrollY < lastScrollY.value) {
    isHidden.value = false;
  }

  lastScrollY.value = currentScrollY;
}

function setupScrollListener() {
  if (isMobile.value) {
    window.addEventListener('scroll', handleScroll, { passive: true });
  } else {
    window.removeEventListener('scroll', handleScroll);
    isHidden.value = false;
  }
}

onMounted(() => {
  setupScrollListener();
});

watch(isMobile, () => {
  setupScrollListener();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <header
    class="sticky top-0 z-40 wood-texture border-b border-black/20 shadow-lg"
    :class="{
      'transition-transform duration-300 ease-in-out -translate-y-full': isMobile && isHidden,
    }"
  >
    <!-- Wood grain texture overlay -->
    <div
      class="absolute inset-0 opacity-40 pointer-events-none"
      style="
        background-image: url('/wood.jpg');
        background-size: cover;
        background-position: center;
      "
    ></div>

    <div class="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3 relative z-10">
      <!-- Back button and Title -->
      <div class="flex items-center gap-3 min-w-0 flex-1">
        <!-- Back to workshops button -->
        <button
          @click="router.push('/workshops')"
          class="cursor-pointer p-2 text-white hover:bg-white/10 rounded-lg transition-colors shrink-0"
          title="Back to Workshops"
        >
          <ChevronLeft class="w-5 h-5" />
        </button>

        <div class="flex flex-col gap-0.5 min-w-0">
          <h1 class="text-xl font-bold text-white truncate drop-shadow-md">
            {{ workshopName || 'Ideation Workshop' }}
          </h1>
          <span class="text-xs text-white/80 whitespace-nowrap drop-shadow">
            {{ filteredCount }} of {{ totalIdeas }} ideas
          </span>
        </div>
      </div>

      <!-- Desktop buttons - shown on lg screens and up -->
      <div class="hidden lg:flex items-center gap-2">
        <!-- Access Management (Owners only) -->
        <button
          v-if="isOwner"
          @click="emit('openInvites')"
          class="cursor-pointer px-3 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2 text-sm"
          :title="$t('common.access')"
        >
          <Users class="w-4 h-4" />
          {{ $t('common.access') }}
        </button>

        <!-- Supporters (Owners only) -->
        <button
          v-if="isOwner"
          @click="emit('openSupporters')"
          class="cursor-pointer px-3 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2 text-sm"
          :title="$t('common.supporters')"
        >
          <HandHelping class="w-4 h-4" />
          {{ $t('common.supporters') }}
        </button>

        <!-- Comments (Owners only) -->
        <button
          v-if="isOwner"
          @click="emit('openComments')"
          class="cursor-pointer px-3 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2 text-sm"
          :title="$t('common.comments')"
        >
          <MessageSquare class="w-4 h-4" />
          {{ $t('common.comments') }}
        </button>

        <!-- Settings -->
        <button
          @click="emit('openSettings')"
          class="cursor-pointer px-3 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2 text-sm"
          :title="$t('settings.title')"
        >
          <Settings class="w-4 h-4" />
          {{ $t('settings.title') }}
        </button>

        <div v-if="isOwner" class="w-px h-6 bg-white/30 mx-1"></div>

        <button
          v-if="isOwner"
          @click="emit('export')"
          class="cursor-pointer px-3 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2 text-sm"
        >
          <Download class="w-4 h-4" />
          {{ $t('common.export') }}
        </button>
        <button
          v-if="isOwner"
          @click="emit('import')"
          class="cursor-pointer px-3 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2 text-sm"
        >
          <Upload class="w-4 h-4" />
          {{ $t('common.import') }}
        </button>
        <button
          v-if="canWrite"
          @click="emit('addIdea')"
          class="push-button px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium"
        >
          <Plus class="w-4 h-4" />
          {{ $t('idea.addNew') }}
        </button>
      </div>

      <!-- Mobile/Tablet hamburger - shown on smaller screens -->
      <button
        @click="emit('toggleMenu')"
        class="cursor-pointer lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors shrink-0 z-70 relative"
        :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
      >
        <component :is="menuOpen ? X : Menu" class="w-6 h-6" />
      </button>
    </div>
  </header>
</template>
